import BaseModal from '@c/modals/BaseModal';
import { useQuery } from '@tanstack/react-query';
import Button from '@ui/Button';
import { Label } from '@ui/Label';
import { getMainCatsClient } from '@util/firestore/categories';
import { useCreateListing } from '../_context';
import CategoryPicker, {
  applyCategoryToForm,
  useSelectedCategory,
} from './CategoryPicker';

export default function EditCategoryModal({
  isOpen,
  dismiss,
}: {
  isOpen: boolean;
  dismiss: () => void;
}) {
  const { categoryInfo, setCategoryInfo, form } = useCreateListing();
  const selected = useSelectedCategory();
  const { data: sports } = useQuery({
    queryKey: ['sports'],
    queryFn: () => getMainCatsClient(),
  });

  const sortedSports = [...(sports ?? [])].sort(
    (a, b) => (a.index ?? 0) - (b.index ?? 0)
  );

  const handleCancel = () => {
    setCategoryInfo([]);
    dismiss();
  };

  const handleSave = () => {
    if (!selected) {
      form.setError('category', {
        type: 'required',
        message: 'Please select all categories',
      });
      return;
    }
    applyCategoryToForm(form, selected);
    dismiss();
  };

  return (
    <BaseModal
      isOpen={isOpen}
      title={'Edit Sports & Categories'}
      dismiss={dismiss}
    >
      <div className="flex w-[calc(100vw-4rem)] max-w-[80rem] flex-col gap-[2rem] p-8">
        <div className="flex flex-col gap-[1rem]">
          <p
            id="edit-category-sport"
            className="text-[1.75rem] font-semibold text-black"
          >
            Sport
          </p>
          <div
            role="radiogroup"
            aria-labelledby="edit-category-sport"
            className="flex flex-wrap gap-[1rem]"
          >
            {sortedSports.map((s) => {
              const isActive = s.id === categoryInfo[0]?.id;
              return (
                <button
                  key={s.id}
                  type="button"
                  role="radio"
                  aria-checked={isActive}
                  onClick={() => {
                    if (!isActive) setCategoryInfo([s]);
                  }}
                  className={`rounded-[1rem] border-[1px] px-[1.5rem] py-[0.75rem] text-[1.5rem] font-medium ${
                    isActive
                      ? 'border-brand-secondary text-brand-secondary'
                      : 'border-zinc-200 text-black'
                  }`}
                >
                  {s.name}
                </button>
              );
            })}
          </div>
        </div>

        {categoryInfo[0] && (
          <CategoryPicker
            key={categoryInfo[0].id}
            panelClassName="h-[40rem] max-h-[40vh]"
            autoFocus
          />
        )}

        {form.formState.errors.category?.message && (
          <Label className="text-[1.3rem] text-red-500">
            {form.formState.errors.category.message}
          </Label>
        )}

        <div className="flex gap-[1.6rem]">
          <Button
            width="fluid"
            text="Cancel"
            type="tertiary"
            onClick={handleCancel}
          />
          <Button
            width="fluid"
            text="Save Changes"
            type="secondary"
            disabled={!selected}
            onClick={handleSave}
          />
        </div>
      </div>
    </BaseModal>
  );
}
